import { supabase } from '@/lib/supabase/client';
import { locationOrdersService } from './location-orders';
import type { LocationOrderItem, LocationOrderItemInsert, LocationOrderItemUpdate } from '@/types/location-orders';

export const locationOrderItemsService = {
  async getByOrderId(orderId: string): Promise<LocationOrderItem[]> {
    try {
      const { data, error } = await supabase
        .from('location_order_items')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching location order items:', error);
        return [];
      }

      return (data as LocationOrderItem[]) || [];
    } catch (error) {
      console.error('Error fetching location order items:', error);
      return [];
    }
  },

  async getByLocationId(locationId: string): Promise<LocationOrderItem[]> {
    try {
      const orders = await locationOrdersService.getByLocationId(locationId);
      if (orders.length === 0) {
        return [];
      }

      const allItems = await Promise.all(
        orders.map((order) => locationOrderItemsService.getByOrderId(order.id))
      );

      return allItems.flat();
    } catch (error) {
      console.error('Error fetching location order items by location ID:', error);
      return [];
    }
  },

  async getById(id: string): Promise<LocationOrderItem | null> {
    try {
      const { data, error } = await supabase.from('location_order_items').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching location order item:', error);
        return null;
      }

      return (data as LocationOrderItem) || null;
    } catch (error) {
      console.error('Error fetching location order item:', error);
      return null;
    }
  },

  async create(data: LocationOrderItemInsert): Promise<LocationOrderItem> {
    try {
      const insertData = {
        order_id: data.order_id,
        display_type_id: data.display_type_id,
        quantity: data.quantity,
        unit_price: data.unit_price ?? null,
      };

      const { data: newItem, error } = await supabase.from('location_order_items').insert(insertData).select().single();

      if (error) {
        console.error('Error creating location order item:', error);
        throw new Error(`Failed to create location order item: ${error.message}`);
      }

      return newItem as LocationOrderItem;
    } catch (error) {
      console.error('Error creating location order item:', error);
      throw error;
    }
  },

  async update(id: string, data: LocationOrderItemUpdate): Promise<LocationOrderItem> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.order_id !== undefined) updateData.order_id = data.order_id;
      if (data.display_type_id !== undefined) updateData.display_type_id = data.display_type_id;
      if (data.quantity !== undefined) updateData.quantity = data.quantity;
      if (data.unit_price !== undefined) updateData.unit_price = data.unit_price;

      const { data: updatedItem, error } = await supabase
        .from('location_order_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating location order item:', error);
        throw new Error(`Failed to update location order item: ${error.message}`);
      }

      if (!updatedItem) {
        throw new Error('Location order item not found');
      }

      return updatedItem as LocationOrderItem;
    } catch (error) {
      console.error('Error updating location order item:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('location_order_items').delete().eq('id', id);

      if (error) {
        console.error('Error deleting location order item:', error);
        throw new Error(`Failed to delete location order item: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting location order item:', error);
      throw error;
    }
  },

  async deleteByOrderId(orderId: string): Promise<void> {
    try {
      const { error } = await supabase.from('location_order_items').delete().eq('order_id', orderId);

      if (error) {
        console.error('Error deleting location order items by order ID:', error);
        throw new Error(`Failed to delete location order items: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting location order items by order ID:', error);
      throw error;
    }
  },
};
